import React, { useState, useEffect, useCallback, useRef } from 'react';
import './baitcalculator.css';
import { useToast } from '../components/Toast';
import useAdminVerification from '../hooks/useAdminVerification';
import AdminSettingsPanel from '../components/AdminSettingsPanel';

// 최적화된 이미지 컴포넌트
const BaitImage = ({ filename, alt, className, onError }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (filename) {
      setImageError(false);
      const imageUrl = `/data/img/preview/${filename}.webp`;
      setImageSrc(imageUrl);
    }
  }, [filename]);

  if (imageError) {
    return null; // 에러 시 이미지 숨김
  }

  return (
    <img 
      src={imageSrc}
      alt={alt}
      className={className}
      onError={() => {
        setImageError(true);
        if (onError) {
          onError();
        }
      }}
      loading="lazy" // 지연 로딩 추가
    />
  );
};

// SearchableSelect 컴포넌트를 외부로 분리
const SearchableSelect = ({ 
  category, 
  type, 
  label, 
  groundbaitData, 
  selectedItems, 
  searchTerms, 
  openDropdowns, 
  searchLanguage,
  onSearchChange,
  onItemSelect,
  onClearSelection,
  onToggleDropdown,
  onSetOpenDropdowns
}) => {
  const inputRef = useRef(null);
  
  // 타입별 데이터 필터링
  const getFilteredData = (type) => {
    if (type === 'base') {
      return groundbaitData.filter(item => item.type === '베이스');
    } else if (type === 'additive') {
      return groundbaitData.filter(item => 
        ['첨가물', '싱킹 보일리', '팝업 보일리', '음식', '펠릿'].includes(item.type)
      );
    } else if (type === 'attractant') {
      return groundbaitData.filter(item => item.type === '유인제');
    }
    return [];
  };

  // 검색용 언어별 이름 가져오기
  const getSearchName = (item) => {
    if (!item) return '';
    return item[searchLanguage] || item.KR || item.filename;
  };

  // 검색 필터링
  const getSearchFilteredData = (type, category) => {
    const data = getFilteredData(type);
    const searchTerm = searchTerms[category]?.toLowerCase().trim() || '';
    
    if (!searchTerm) return data;
    
    return data.filter(item => {
      const displayName = getSearchName(item).toLowerCase();
      const manufacturer = (item.manufacturer || '').toLowerCase();
      const filename = (item.filename || '').toLowerCase();
      const itemType = (item.type || '').toLowerCase();
      
      return displayName.includes(searchTerm) ||
             manufacturer.includes(searchTerm) ||
             filename.includes(searchTerm) ||
             itemType.includes(searchTerm);
    });
  };

  const data = getSearchFilteredData(type, category);
  const selectedItem = selectedItems[category];
  const isOpen = openDropdowns[category];
  const searchTerm = searchTerms[category];

  // 검색어 변경 시 포커스 유지
  const handleLocalSearchChange = useCallback((value) => {
    const currentFocus = document.activeElement;
    onSearchChange(category, value);
    
    // 다음 렌더링 후 포커스 복원
    setTimeout(() => {
      if (inputRef.current && currentFocus === inputRef.current) {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(value.length, value.length);
      }
    }, 0);
  }, [category, onSearchChange]);

  return (
    <div className="bait-select-container">
      <label className="bait-select-label">{label}</label>
      
      <div className="bait-select-row">
        <div className="bait-image-container">
          {selectedItem && (
            <BaitImage 
              filename={selectedItem.filename}
              alt={getSearchName(selectedItem)}
              className="bait-preview-image"
              onError={() => {}}
            />
          )}
        </div>
        <div className="searchable-select">
          <div className="select-input-container">
            <input
              ref={inputRef}
              type="text"
              className="select-search-input"
              placeholder={selectedItem ? getSearchName(selectedItem) : "검색하여 선택하세요..."}
              value={searchTerm || ''}
              onChange={(e) => {
                handleLocalSearchChange(e.target.value);
              }}
              onFocus={(e) => {
                e.stopPropagation();
                onSetOpenDropdowns(prev => ({ ...prev, [category]: true }));
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
              autoComplete="off"
              spellCheck="false"
            />
            {selectedItem && (
              <button 
                type="button"
                className="clear-selection-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onClearSelection(category);
                }}
                title="선택 해제"
              >
                ✕
              </button>
            )}
            <button 
              type="button"
              className="select-dropdown-btn"
              onClick={() => onToggleDropdown(category)}
            >
              <span className={`dropdown-arrow ${isOpen ? 'open' : ''}`}>▼</span>
            </button>
          </div>
          
          {isOpen && (
            <div className="select-dropdown">
              {data.length === 0 ? (
                <div className="select-option no-results">결과가 없습니다</div>
              ) : (
                data.map(item => (
                  <div
                    key={item.id}
                    className="select-option"
                    onClick={() => onItemSelect(category, item)}
                  >
                    <BaitImage 
                      filename={item.filename}
                      alt={getSearchName(item)}
                      className="option-image"
                      onError={() => {}}
                    />
                    <div className="option-info">
                      <div className="option-name">{getSearchName(item)}</div>
                      <div className="option-manufacturer">{item.manufacturer}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const BaitCalculator = () => {
  const [displayLanguage, setDisplayLanguage] = useState('KR'); // 선택된 미끼 표시 언어
  const [searchLanguage, setSearchLanguage] = useState('KR'); // 검색/드롭다운 언어
  const [groundbaitData, setGroundbaitData] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false); // 로그인 상태
  const [isAdmin, setIsAdmin] = useState(false); // 관리자 권한
  const [savedSettings, setSavedSettings] = useState([]); // 저장된 설정 목록
  const [selectedSettingId, setSelectedSettingId] = useState(''); // 선택된 설정 ID
  const [saveSettingName, setSaveSettingName] = useState(''); // 저장할 설정 이름
  const [showSaveInput, setShowSaveInput] = useState(false); // 저장 입력창 표시 여부
  const [showDeleteModal, setShowDeleteModal] = useState(false); // 삭제 확인 모달 표시 여부
  const [deleteTargetId, setDeleteTargetId] = useState(''); // 삭제할 설정 ID
  const [showDuplicateModal, setShowDuplicateModal] = useState(false); // 중복 이름 경고 모달 표시 여부
  const [duplicateSettingName, setDuplicateSettingName] = useState(''); // 중복된 설정 이름
  
  // 클립보드 검색 모달 관련 state
  const [showClipboardModal, setShowClipboardModal] = useState(false); // 클립보드 검색 모달 표시 여부
  const [clipboardText, setClipboardText] = useState(''); // 클립보드 입력 텍스트
  const [searchResults, setSearchResults] = useState([]); // 검색 결과
  const [selectedSearchItems, setSelectedSearchItems] = useState({}); // 선택된 검색 결과 (체크박스)
  
  // 관리자 전용 state
  const [allUsers, setAllUsers] = useState([]); // 모든 유저 목록
  const [selectedUserId, setSelectedUserId] = useState(''); // 선택된 유저 ID
  const [selectedUserSettings, setSelectedUserSettings] = useState([]); // 선택된 유저의 설정 목록
  const [selectedUserSettingId, setSelectedUserSettingId] = useState(''); // 선택된 유저의 설정 ID
  const [selectedItems, setSelectedItems] = useState({
    base: null,
    additive1: null,
    additive2: null,
    additive3: null,
    additive4: null,
    attractant: null
  });

  const [searchTerms, setSearchTerms] = useState({
    base: '',
    additive1: '',
    additive2: '',
    additive3: '',
    additive4: '',
    attractant: ''
  });

  const [openDropdowns, setOpenDropdowns] = useState({});
  const [isInitialLogin, setIsInitialLogin] = useState(true); // 초기 로그인 상태 확인 여부
  const toast = useToast();
  const { isAdminVerified } = useAdminVerification();

  // 문자열 유사도 계산 함수 (Levenshtein distance 기반)
  const calculateSimilarity = (str1, str2) => {
    const s1 = str1.toLowerCase().trim();
    const s2 = str2.toLowerCase().trim();
    
    if (s1 === s2) return 100;
    
    const longer = s1.length > s2.length ? s1 : s2;
    const shorter = s1.length > s2.length ? s2 : s1;
    
    if (longer.length === 0) return 100;
    
    // Levenshtein distance 계산
    const costs = [];
    for (let i = 0; i <= longer.length; i++) {
      let lastValue = i;
      for (let j = 0; j <= shorter.length; j++) {
        if (i === 0) {
          costs[j] = j;
        } else if (j > 0) {
          let newValue = costs[j - 1];
          if (longer.charAt(i - 1) !== shorter.charAt(j - 1)) {
            newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
          }
          costs[j - 1] = lastValue;
          lastValue = newValue;
        }
      }
      if (i > 0) costs[shorter.length] = lastValue;
    }
    
    const distance = costs[shorter.length];
    return ((longer.length - distance) / longer.length) * 100;
  };

  // 클립보드 텍스트로 미끼 검색
  const searchBaitsFromClipboard = () => {
    if (!clipboardText.trim()) {
      toast.error('검색할 텍스트를 입력해주세요.');
      return;
    }

    // 텍스트를 줄바꿈으로 분리하고 공백 제거
    const lines = clipboardText.split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    const results = [];
    const threshold = 60; // 유사도 임계값

    lines.forEach(searchText => {
      // 각 미끼 데이터와 비교
      groundbaitData.forEach(item => {
        // 모든 언어의 이름과 비교 (filename 제외)
        const languageNames = [
          { lang: 'KR', name: item.KR },
          { lang: 'EN', name: item.EN },
          { lang: 'CN', name: item.CN },
          { lang: 'RU', name: item.RU }
        ].filter(obj => obj.name);
        
        languageNames.forEach(({ lang, name }) => {
          const similarity = calculateSimilarity(searchText, name);
          
          if (similarity >= threshold) {
            // 중복 체크 (같은 아이템이 이미 있는지)
            const existingIndex = results.findIndex(r => r.item.id === item.id);
            
            if (existingIndex === -1) {
              results.push({
                item: item,
                searchText: searchText,
                matchedName: name,
                matchedLang: lang,
                similarity: similarity
              });
            } else if (results[existingIndex].similarity < similarity) {
              // 더 높은 유사도면 교체
              results[existingIndex] = {
                item: item,
                searchText: searchText,
                matchedName: name,
                matchedLang: lang,
                similarity: similarity
              };
            }
          }
        });
      });
    });

    // 유사도 순으로 정렬
    results.sort((a, b) => b.similarity - a.similarity);
    setSearchResults(results);
    
    if (results.length === 0) {
      toast.error('검색 결과가 없습니다.');
    } else {
      toast.success(`${results.length}개의 미끼를 찾았습니다.`);
    }
  };

  // 검색 결과를 선택 패널에 적용 (기존 리스트 대체)
  const applySearchResults = () => {
    const selectedIds = Object.keys(selectedSearchItems).filter(id => selectedSearchItems[id]);
    
    if (selectedIds.length === 0) {
      toast.error('적용할 미끼를 선택해주세요.');
      return;
    }

    // 기존 선택 항목 초기화 (완전히 대체)
    const newSelectedItems = {
      base: null,
      additive1: null,
      additive2: null,
      additive3: null,
      additive4: null,
      attractant: null
    };
    
    // 선택된 아이템들을 타입별로 분류
    const selectedResults = searchResults.filter(result => 
      selectedIds.includes(result.item.id.toString())
    );

    // 첨가물 인덱스 추적
    let additiveIndex = 1;

    selectedResults.forEach(result => {
      const item = result.item;
      
      if (item.type === '베이스') {
        newSelectedItems.base = item;
      } else if (item.type === '유인제') {
        newSelectedItems.attractant = item;
      } else if (['첨가물', '싱킹 보일리', '팝업 보일리', '음식', '펠릿'].includes(item.type)) {
        // 첨가물 슬롯에 순서대로 배치 (최대 4개)
        if (additiveIndex <= 4) {
          newSelectedItems[`additive${additiveIndex}`] = item;
          additiveIndex++;
        }
      }
    });

    setSelectedItems(newSelectedItems);
    setShowClipboardModal(false);
    setClipboardText('');
    setSearchResults([]);
    setSelectedSearchItems({});
    
    // 적용된 항목 개수 계산
    const appliedCount = Object.values(newSelectedItems).filter(item => item !== null).length;
    toast.success(`${appliedCount}개의 미끼를 적용했습니다. (기존 선택 항목이 대체되었습니다)`);
  };

  // 체크박스 토글 (최대 개수 제한 포함)
  const toggleSearchItemSelection = (itemId) => {
    setSelectedSearchItems(prev => {
      const isCurrentlySelected = prev[itemId];
      
      // 이미 선택된 항목을 해제하는 경우
      if (isCurrentlySelected) {
        const newState = { ...prev };
        delete newState[itemId];
        return newState;
      }
      
      // 새로 선택하는 경우 - 최대 개수 확인
      const selectedIds = Object.keys(prev).filter(id => prev[id]);
      const selectedResults = searchResults.filter(result => 
        selectedIds.includes(result.item.id.toString())
      );
      
      // 선택하려는 아이템 찾기
      const itemToSelect = searchResults.find(r => r.item.id.toString() === itemId.toString());
      if (!itemToSelect) return prev;
      
      // 타입별 현재 선택 개수 계산
      const baseCount = selectedResults.filter(r => r.item.type === '베이스').length;
      const attractantCount = selectedResults.filter(r => r.item.type === '유인제').length;
      const additiveCount = selectedResults.filter(r => 
        ['첨가물', '싱킹 보일리', '팝업 보일리', '음식', '펠릿'].includes(r.item.type)
      ).length;
      
      // 최대 개수 체크
      if (itemToSelect.item.type === '베이스' && baseCount >= 1) {
        toast.error('베이스는 최대 1개만 선택할 수 있습니다.');
        return prev;
      }
      
      if (itemToSelect.item.type === '유인제' && attractantCount >= 1) {
        toast.error('유인제는 최대 1개만 선택할 수 있습니다.');
        return prev;
      }
      
      if (['첨가물', '싱킹 보일리', '팝업 보일리', '음식', '펠릿'].includes(itemToSelect.item.type) && additiveCount >= 4) {
        toast.error('첨가물은 최대 4개만 선택할 수 있습니다.');
        return prev;
      }
      
      // 선택 가능하면 추가
      return {
        ...prev,
        [itemId]: true
      };
    });
  };

  // 관리자 권한 확인 - useAdminVerification 훅 사용
  useEffect(() => {
    if (isLoggedIn) {
      setIsAdmin(isAdminVerified);
    } else {
      setIsAdmin(false);
    }
  }, [isLoggedIn, isAdminVerified]);

  // 로그인 상태 실시간 확인
  useEffect(() => {
    const checkLoginStatus = () => {
      const token = localStorage.getItem('token');
      const newLoginStatus = !!token;
      
      if (newLoginStatus !== isLoggedIn) {
        setIsLoggedIn(newLoginStatus);
        
        // 로그아웃 시 저장된 설정 관련 상태 초기화
        if (!newLoginStatus) {
          setSavedSettings([]);
          setSelectedSettingId('');
          setSaveSettingName('');
          setShowSaveInput(false);
          setIsAdmin(false);
          setAllUsers([]);
          setSelectedUserId('');
          setSelectedUserSettings([]);
          setSelectedUserSettingId('');
        }
        
        // 초기 확인 완료 표시
        if (isInitialLogin) {
          setIsInitialLogin(false);
        }
      }
    };

    // 초기 로그인 상태 확인
    checkLoginStatus();

    // localStorage 변화 감지 (다른 탭에서의 로그인/로그아웃)
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        checkLoginStatus();
      }
    };

    // focus 이벤트로 탭 활성화 시 로그인 상태 재확인
    const handleFocus = () => {
      checkLoginStatus();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('focus', handleFocus);

    // 주기적으로 로그인 상태 확인 (1초마다)
    const interval = setInterval(checkLoginStatus, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleFocus);
      clearInterval(interval);
    };
  }, [isLoggedIn, isInitialLogin, toast]);

  // 저장된 설정 로드
  useEffect(() => {
    if (isLoggedIn) {
      loadSavedSettings();
      // 관리자인 경우 유저 목록 로드
      if (isAdmin) {
        loadAllUsers();
      }
    }
  }, [isLoggedIn, isAdmin]);

  // 관리자 전용: 모든 유저 목록 로드
  const loadAllUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch('/api/bait/admin/users-with-settings', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setAllUsers(result.data);
        }
      }
    } catch (error) {
      console.error('유저 목록 로드 실패:', error);
    }
  };

  // 관리자 전용: 특정 유저의 설정 목록 로드
  const loadUserSettings = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`/api/bait/admin/user-settings/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setSelectedUserSettings(result.data);
        }
      }
    } catch (error) {
      console.error('유저 설정 로드 실패:', error);
    }
  };

  // 관리자 전용: 유저 선택 핸들러
  const handleUserSelect = (userId) => {
    setSelectedUserId(userId);
    setSelectedUserSettingId('');
    setSelectedUserSettings([]);
    if (userId) {
      loadUserSettings(userId);
    }
  };

  // 관리자 전용: 유저의 설정 불러오기
  const handleLoadUserSetting = async (settingId) => {
    const setting = selectedUserSettings.find(s => s.id === parseInt(settingId));
    if (!setting) return;

    const newSelectedItems = {
      base: null,
      additive1: null,
      additive2: null,
      additive3: null,
      additive4: null,
      attractant: null
    };

    // ID로 실제 아이템 찾기
    if (setting.base_id) {
      newSelectedItems.base = groundbaitData.find(item => item.id === setting.base_id);
    }
    if (setting.additive1_id) {
      newSelectedItems.additive1 = groundbaitData.find(item => item.id === setting.additive1_id);
    }
    if (setting.additive2_id) {
      newSelectedItems.additive2 = groundbaitData.find(item => item.id === setting.additive2_id);
    }
    if (setting.additive3_id) {
      newSelectedItems.additive3 = groundbaitData.find(item => item.id === setting.additive3_id);
    }
    if (setting.additive4_id) {
      newSelectedItems.additive4 = groundbaitData.find(item => item.id === setting.additive4_id);
    }
    if (setting.attractant_id) {
      newSelectedItems.attractant = groundbaitData.find(item => item.id === setting.attractant_id);
    }

    setSelectedItems(newSelectedItems);
    
    const user = allUsers.find(u => u.user_id === selectedUserId);
    const username = user ? user.username : '사용자';
    toast.success(`${username}의 "${setting.saved_name}" 설정을 불러왔습니다.`);
  };

  const loadSavedSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // 토큰이 없으면 로그인 상태 업데이트
      if (!token) {
        setIsLoggedIn(false);
        setSavedSettings([]);
        return;
      }

      const response = await fetch('/api/bait/saved-settings', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setSavedSettings(result.data);
        } else {
          console.error('저장된 설정 로드 실패:', result.error);
          setSavedSettings([]);
        }
      } else if (response.status === 401 || response.status === 403) {
        // 토큰이 만료되었거나 유효하지 않은 경우
        console.log('토큰이 유효하지 않습니다. 로그아웃 처리합니다.');
        localStorage.removeItem('token');
        setIsLoggedIn(false);
        setSavedSettings([]);
      } else {
        console.error('저장된 설정 로드 실패:', response.status);
        setSavedSettings([]);
      }
    } catch (error) {
      console.error('저장된 설정 로드 실패:', error);
      setSavedSettings([]);
    }
  };

  // 설정 저장
  const handleSaveSetting = async () => {
    // 로그인 상태 재확인
    const token = localStorage.getItem('token');
    if (!token) {
      setIsLoggedIn(false);
      toast.error('로그인이 필요합니다.');
      return;
    }

    if (!saveSettingName.trim()) {
      toast.error('저장할 이름을 입력해주세요.');
      return;
    }

    if (!selectedItems.base) {
      toast.error('베이스는 필수로 선택해야 합니다.');
      return;
    }

    // 중복 이름 확인
    const trimmedName = saveSettingName.trim();
    const duplicateSetting = savedSettings.find(setting => 
      setting.saved_name.toLowerCase() === trimmedName.toLowerCase()
    );

    if (duplicateSetting) {
      // 중복된 이름이 있으면 모달 표시
      setDuplicateSettingName(trimmedName);
      setShowDuplicateModal(true);
      return;
    }

    // 중복이 없으면 바로 저장
    await performSave();
  };

  // 실제 저장 수행 함수
  const performSave = async () => {
    const token = localStorage.getItem('token');

    try {
      const response = await fetch('/api/bait/saved-settings', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          savedName: saveSettingName.trim(),
          baseId: selectedItems.base?.id,
          additive1Id: selectedItems.additive1?.id,
          additive2Id: selectedItems.additive2?.id,
          additive3Id: selectedItems.additive3?.id,
          additive4Id: selectedItems.additive4?.id,
          attractantId: selectedItems.attractant?.id
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          toast.success(result.message);
          setSaveSettingName('');
          setShowSaveInput(false);
          loadSavedSettings(); // 목록 새로고침
        } else {
          toast.error(result.error);
        }
      } else if (response.status === 401 || response.status === 403) {
        // 토큰이 만료되었거나 유효하지 않은 경우
        localStorage.removeItem('token');
        setIsLoggedIn(false);
        toast.error('로그인이 만료되었습니다. 다시 로그인해주세요.');
      } else {
        toast.error('설정 저장에 실패했습니다.');
      }
    } catch (error) {
      console.error('설정 저장 실패:', error);
      toast.error('설정 저장에 실패했습니다.');
    }
  };

  // 중복 확인 모달에서 덮어쓰기 확정
  const confirmOverwrite = async () => {
    setShowDuplicateModal(false);
    await performSave();
  };

  // 중복 확인 모달에서 취소
  const cancelOverwrite = () => {
    setShowDuplicateModal(false);
    setDuplicateSettingName('');
  };

  // 설정 불러오기
  const handleLoadSetting = async (settingId) => {
    const setting = savedSettings.find(s => s.id === parseInt(settingId));
    if (!setting) return;

    const newSelectedItems = {
      base: null,
      additive1: null,
      additive2: null,
      additive3: null,
      additive4: null,
      attractant: null
    };

    // ID로 실제 아이템 찾기
    if (setting.base_id) {
      newSelectedItems.base = groundbaitData.find(item => item.id === setting.base_id);
    }
    if (setting.additive1_id) {
      newSelectedItems.additive1 = groundbaitData.find(item => item.id === setting.additive1_id);
    }
    if (setting.additive2_id) {
      newSelectedItems.additive2 = groundbaitData.find(item => item.id === setting.additive2_id);
    }
    if (setting.additive3_id) {
      newSelectedItems.additive3 = groundbaitData.find(item => item.id === setting.additive3_id);
    }
    if (setting.additive4_id) {
      newSelectedItems.additive4 = groundbaitData.find(item => item.id === setting.additive4_id);
    }
    if (setting.attractant_id) {
      newSelectedItems.attractant = groundbaitData.find(item => item.id === setting.attractant_id);
    }

    setSelectedItems(newSelectedItems);
    toast.success(`"${setting.saved_name}" 설정을 불러왔습니다.`);
  };

  // 설정 삭제 확인 모달 열기
  const openDeleteModal = (settingId) => {
    setDeleteTargetId(settingId);
    setShowDeleteModal(true);
  };

  // 설정 삭제 확인
  const confirmDeleteSetting = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // 토큰 검증
      if (!token) {
        setIsLoggedIn(false);
        setShowDeleteModal(false);
        setDeleteTargetId('');
        toast.error('로그인이 필요합니다.');
        return;
      }

      const response = await fetch(`/api/bait/saved-settings/${deleteTargetId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          toast.success(result.message);
          loadSavedSettings(); // 목록 새로고침
          if (selectedSettingId === deleteTargetId) {
            setSelectedSettingId('');
          }
        } else {
          toast.error(result.error);
        }
      } else if (response.status === 401 || response.status === 403) {
        // 토큰이 만료되었거나 유효하지 않은 경우
        localStorage.removeItem('token');
        setIsLoggedIn(false);
        toast.error('로그인이 만료되었습니다. 다시 로그인해주세요.');
      } else {
        toast.error('설정 삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('설정 삭제 실패:', error);
      toast.error('설정 삭제에 실패했습니다.');
    } finally {
      setShowDeleteModal(false);
      setDeleteTargetId('');
    }
  };

  // 설정 삭제 취소
  const cancelDeleteSetting = () => {
    setShowDeleteModal(false);
    setDeleteTargetId('');
  };

  // 설정 삭제 (기존 함수 - 이제 모달 열기용으로 변경)
  const handleDeleteSetting = (settingId) => {
    openDeleteModal(settingId);
  };

  // 데이터 로드
  useEffect(() => {
    const loadGroundbaitData = async () => {
      try {
        // JWT 토큰을 헤더에 포함
        const token = localStorage.getItem('token');
        const headers = {
          'Content-Type': 'application/json',
        };
        
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        // 실제 API 호출
        const response = await fetch('/api/bait/groundbait-price', {
          method: 'GET',
          headers: headers
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        
        if (result.success) {
          setGroundbaitData(result.data);
        } else {
          throw new Error(result.message || '데이터 로드 실패');
        }
      } catch (error) {
        console.error('Failed to load groundbait data:', error);
        // 에러 시 빈 배열로 설정
        setGroundbaitData([]);
      }
    };

    loadGroundbaitData();
  }, []);

  // URL 파라미터에서 선택된 아이템 복원 (최초 1회만)
  const isInitialMount = useRef(true);
  const hasRestoredFromUrl = useRef(false);

  useEffect(() => {
    if (groundbaitData.length === 0) return;
    if (hasRestoredFromUrl.current) return; // 이미 복원했으면 스킵

    const params = new URLSearchParams(window.location.search);
    const baseId = params.get('b');
    const add1Id = params.get('a1');
    const add2Id = params.get('a2');
    const add3Id = params.get('a3');
    const add4Id = params.get('a4');
    const attractantId = params.get('at');

    // URL 파라미터가 하나라도 있으면 복원 시도
    if (baseId || add1Id || add2Id || add3Id || add4Id || attractantId) {
      const newSelectedItems = {
        base: null,
        additive1: null,
        additive2: null,
        additive3: null,
        additive4: null,
        attractant: null
      };

      if (baseId) {
        const item = groundbaitData.find(d => d.id === parseInt(baseId));
        if (item) newSelectedItems.base = item;
      }

      if (add1Id) {
        const item = groundbaitData.find(d => d.id === parseInt(add1Id));
        if (item) newSelectedItems.additive1 = item;
      }

      if (add2Id) {
        const item = groundbaitData.find(d => d.id === parseInt(add2Id));
        if (item) newSelectedItems.additive2 = item;
      }

      if (add3Id) {
        const item = groundbaitData.find(d => d.id === parseInt(add3Id));
        if (item) newSelectedItems.additive3 = item;
      }

      if (add4Id) {
        const item = groundbaitData.find(d => d.id === parseInt(add4Id));
        if (item) newSelectedItems.additive4 = item;
      }

      if (attractantId) {
        const item = groundbaitData.find(d => d.id === parseInt(attractantId));
        if (item) newSelectedItems.attractant = item;
      }

      setSelectedItems(newSelectedItems);
      hasRestoredFromUrl.current = true;
      
      // 복원된 항목 개수 계산
      const restoredCount = Object.values(newSelectedItems).filter(item => item !== null).length;
      
      toast.success(`공유된 설정을 불러왔습니다! (${restoredCount}개 항목)`, {
        duration: 3000
      });
    } else {
      // URL 파라미터가 없으면 복원 완료로 표시
      hasRestoredFromUrl.current = true;
    }
  }, [groundbaitData, toast]);

  // 선택된 아이템이 변경될 때 URL 업데이트 (초기 마운트 제외)
  useEffect(() => {
    // 데이터가 로드되지 않았으면 업데이트하지 않음
    if (groundbaitData.length === 0) {
      return;
    }

    // URL 복원이 완료될 때까지 대기
    if (!hasRestoredFromUrl.current) {
      return;
    }

    // 복원 완료 후 첫 렌더링은 스킵
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const params = new URLSearchParams();

    if (selectedItems.base) params.set('b', selectedItems.base.id);
    if (selectedItems.additive1) params.set('a1', selectedItems.additive1.id);
    if (selectedItems.additive2) params.set('a2', selectedItems.additive2.id);
    if (selectedItems.additive3) params.set('a3', selectedItems.additive3.id);
    if (selectedItems.additive4) params.set('a4', selectedItems.additive4.id);
    if (selectedItems.attractant) params.set('at', selectedItems.attractant.id);

    const newUrl = params.toString() 
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;

    window.history.replaceState({}, '', newUrl);
  }, [selectedItems, groundbaitData]);

  // 선택된 미끼 표시용 언어별 이름 가져오기
  const getDisplayName = (item) => {
    if (!item) return '';
    return item[displayLanguage] || item.KR || item.filename;
  };

  // 가격 계산 함수
  const calculatePriceInfo = () => {
    // 베이스와 유인제가 선택되지 않았으면 계산 불가
    if (!selectedItems.base || !selectedItems.attractant) {
      return null;
    }

    // 선택된 첨가물 개수 계산
    const additiveCount = [selectedItems.additive1, selectedItems.additive2, selectedItems.additive3, selectedItems.additive4]
      .filter(item => item !== null).length;

    // 필요한 아이템들
    const requiredItems = [
      selectedItems.base,
      selectedItems.attractant,
      ...Object.values(selectedItems).filter((item, index) => 
        index >= 1 && index <= 4 && item !== null // additive1~4만 선택
      )
    ].filter(item => item !== null);

    // 제작 수량 계산 (베이스 + 유인제 = 20개, 첨가물 1개당 +10개)
    const totalQuantity = 20 + (additiveCount * 10);

    // 각 아이템의 최소 가격과 구매 수량 계산 (싱킹/팝업 보일리는 25개 소비량 고려)
    const getItemMinPriceInfo = (item) => {
      const priceFields = ['qty_1', 'qty_5', 'qty_10', 'qty_15', 'qty_20', 'qty_25', 'qty_30', 'qty_50', 'qty_100', 'qty_250'];
      const validPrices = [];
      
      priceFields.forEach((field, index) => {
        const price = item[field];
        if (price !== null && price !== undefined && price > 0) {
          // 필드명에서 수량 추출
          const quantity = parseInt(field.replace('qty_', ''));
          // 가격을 숫자로 변환
          const numericPrice = parseFloat(price);
          validPrices.push({ price: numericPrice, quantity });
        }
      });
      
      if (validPrices.length === 0) return { price: 0, quantity: 0, purchaseCount: 0 };
      
      // 싱킹/팝업 보일리인 경우 25개 소비량 고려
      const isBoilie = item.type === '싱킹 보일리' || item.type === '팝업 보일리';
      
      if (isBoilie) {
        // 25개 이상인 가격 중 최소값 찾기
        const sufficientPrices = validPrices.filter(p => p.quantity >= 25);
        
        if (sufficientPrices.length > 0) {
          // 25개 이상 구매 가능한 것 중 최저가
          const minPriceItem = sufficientPrices.reduce((min, current) => 
            current.price < min.price ? current : min
          );
          return {
            price: minPriceItem.price,
            quantity: minPriceItem.quantity,
            purchaseCount: 1
          };
        } else {
          // 25개 이상 구매 불가능한 경우, 여러 번 구매 필요
          const maxQuantityItem = validPrices.reduce((max, current) => 
            current.quantity > max.quantity ? current : max
          );
          const purchaseCount = Math.ceil(25 / maxQuantityItem.quantity);
          return {
            price: maxQuantityItem.price * purchaseCount,
            quantity: maxQuantityItem.quantity * purchaseCount,
            purchaseCount: purchaseCount
          };
        }
      } else {
        // 일반 아이템은 최저가
        const minPriceItem = validPrices.reduce((min, current) => 
          current.price < min.price ? current : min
        );
        return {
          price: minPriceItem.price,
          quantity: minPriceItem.quantity,
          purchaseCount: 1
        };
      }
    };

    // 모든 아이템의 최소 가격 정보 수집
    const itemsInfo = requiredItems.map(item => ({
      ...item,
      priceInfo: getItemMinPriceInfo(item)
    }));

    // 총 구매 비용 (최소 구매 수량 기준)
    const totalPrice = itemsInfo.reduce((sum, item) => sum + item.priceInfo.price, 0);

    // 제작 가능 횟수 계산 (싱킹/팝업 보일리는 25개당 1회 제작)
    const craftingTimes = itemsInfo.reduce((minTimes, item) => {
      const quantity = item.priceInfo.quantity;
      
      // 싱킹/팝업 보일리는 25개당 1회 제작
      if (item.type === '싱킹 보일리' || item.type === '팝업 보일리') {
        const craftTimes = Math.floor(quantity / 25);
        return Math.min(minTimes, craftTimes);
      } else {
        // 일반 아이템은 1개당 1회 제작 (베이스, 유인제, 첨가물)
        return Math.min(minTimes, quantity);
      }
    }, Infinity);

    // 실제 제작 가능한 총 미끼 개수
    const totalMakableQuantity = craftingTimes * totalQuantity;

    // 1개당 비용 계산
    const costPerUse = totalMakableQuantity > 0 ? totalPrice / totalMakableQuantity : 0;

    return {
      totalQuantity,
      totalPrice,
      costPerUse,
      additiveCount,
      craftingTimes,
      totalMakableQuantity
    };
  };

  const priceInfo = calculatePriceInfo();

  // 아이템 선택 핸들러
  const handleItemSelect = (category, item) => {
    setSelectedItems(prev => ({
      ...prev,
      [category]: item
    }));
    setSearchTerms(prev => ({
      ...prev,
      [category]: ''
    }));
    setOpenDropdowns(prev => ({
      ...prev,
      [category]: false
    }));
  };

  // 검색어 변경 핸들러 - useCallback으로 안정화
  const handleSearchChange = useCallback((category, value) => {
    setSearchTerms(prev => ({
      ...prev,
      [category]: value
    }));
    setOpenDropdowns(prev => ({
      ...prev,
      [category]: true
    }));
  }, []); // 의존성 배열을 비워서 함수를 안정화

  // 드롭다운 토글
  const toggleDropdown = (category) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // 아이템 선택 해제
  const handleClearSelection = (category) => {
    setSelectedItems(prev => ({
      ...prev,
      [category]: null
    }));
    setSearchTerms(prev => ({
      ...prev,
      [category]: ''
    }));
  };

  // 외부 클릭으로 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      // 검색 관련 요소를 클릭한 경우는 무시
      if (event.target.closest('.searchable-select') || 
          event.target.closest('.select-input-container') ||
          event.target.closest('.select-dropdown') ||
          event.target.classList.contains('select-search-input')) {
        return;
      }
      
      setOpenDropdowns({});
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="bait-calculator">
      <h1 className="page-title">미끼 밑밥 계산기</h1>

      {/* 저장/불러오기 패널 */}
      <div className="saved-settings-panel">
        {isLoggedIn ? (
          <div className="settings-controls">
            <label htmlFor="saved-settings-select">저장/불러오기</label>
            <div className="settings-row">
              <div className="load-settings">
                <div className="select-with-delete">
                  <select
                    id="saved-settings-select"
                    value={selectedSettingId}
                    onChange={(e) => {
                      setSelectedSettingId(e.target.value);
                      if (e.target.value) {
                        handleLoadSetting(e.target.value);
                      }
                    }}
                    className="settings-select"
                    disabled={selectedUserId !== ''} // 다른 유저 조회 중엔 비활성화
                  >
                    <option value="">-- 불러올 설정 선택 --</option>
                    {savedSettings.map(setting => (
                      <option key={setting.id} value={setting.id}>
                        {setting.saved_name} ({new Date(setting.updated_at).toLocaleDateString()})
                      </option>
                    ))}
                  </select>
                  {selectedSettingId && !selectedUserId && (
                    <button
                      className="delete-setting-btn"
                      onClick={() => handleDeleteSetting(selectedSettingId)}
                      title="선택된 설정 삭제"
                    >
                      🗑️
                    </button>
                  )}
                </div>
              </div>
              
              <div className="save-settings">
                {!showSaveInput ? (
                  <button
                    className="save-new-btn"
                    onClick={() => setShowSaveInput(true)}
                    disabled={!selectedItems.base || !selectedItems.attractant || selectedUserId !== ''}
                    title={selectedUserId !== '' ? '다른 유저의 레시피를 보는 중에는 저장할 수 없습니다' : ''}
                  >
                    💾 새 설정 저장
                  </button>
                ) : (
                  <div className="save-input-group">
                    <input
                      type="text"
                      placeholder="설정 이름 입력..."
                      value={saveSettingName}
                      onChange={(e) => setSaveSettingName(e.target.value)}
                      className="save-name-input"
                      maxLength={50}
                    />
                    <button
                      className="save-confirm-btn"
                      onClick={handleSaveSetting}
                    >
                      저장
                    </button>
                    <button
                      className="save-cancel-btn"
                      onClick={() => {
                        setShowSaveInput(false);
                        setSaveSettingName('');
                      }}
                    >
                      취소
                    </button>
                  </div>
                )}
              </div>
            </div>
            {selectedUserId !== '' && (
              <div className="admin-viewing-notice">
                ⚠️ 다른 유저의 레시피를 보는 중입니다. 내 설정으로 돌아가려면 유저 선택을 해제하세요.
              </div>
            )}
          </div>
        ) : (
          <div className="login-required-message">
            🔒 로그인 후 설정 저장이 가능합니다
          </div>
        )}
      </div>

      {/* 관리자 전용: 다른 유저 레시피 조회 패널 */}
      {isLoggedIn && isAdmin && (
        <AdminSettingsPanel
          allUsers={allUsers}
          selectedUserId={selectedUserId}
          onUserSelect={handleUserSelect}
          selectedUserSettings={selectedUserSettings}
          selectedUserSettingId={selectedUserSettingId}
          onSettingSelect={(settingId) => {
            setSelectedUserSettingId(settingId);
            if (settingId) {
              handleLoadUserSetting(settingId);
            }
          }}
          settingType="레시피"
        />
      )}

      {/* 클립보드 검색 버튼 */}
      <div className="clipboard-search-section">
        <button 
          className="clipboard-search-btn"
          onClick={() => setShowClipboardModal(true)}
          title="클립보드에서 미끼 텍스트를 붙여넣어 검색"
        >
          📋 클립보드 검색
        </button>
      </div>

      <div className="bait-calculator-content">
        {/* 좌측: 선택된 아이템 표시 */}
        <div className="bait-display-panel">
          <div className="panel-header">
            <h2>선택된 미끼/밑밥</h2>
            <div className="language-buttons">
              {['KR', 'EN', 'CN', 'RU'].map(lang => (
                <button
                  key={lang}
                  className={`language-btn ${displayLanguage === lang ? 'active' : ''}`}
                  onClick={() => setDisplayLanguage(lang)}
                >
                  {lang}
                </button>
              ))}
            </div>
          </div>
          
          <div className="bait-display-item">
            <div className="bait-display-header">
              <span className="bait-display-label">베이스</span>
            </div>
            <div className="bait-display-content">
              {selectedItems.base && (
                <>
                  <BaitImage 
                    filename={selectedItems.base.filename}
                    alt={getDisplayName(selectedItems.base)}
                    className="bait-display-image"
                    onError={() => {}}
                  />
                  <span className="bait-display-name">{getDisplayName(selectedItems.base)}</span>
                </>
              )}
            </div>
          </div>

          {/* 첨가물 1-4 */}
          {[1, 2, 3, 4].map(num => (
            <div key={num} className="bait-display-item">
              <div className="bait-display-header">
                <span className="bait-display-label">첨가물 {num}</span>
              </div>
              <div className="bait-display-content">
                {selectedItems[`additive${num}`] && (
                  <>
                    <BaitImage 
                      filename={selectedItems[`additive${num}`].filename}
                      alt={getDisplayName(selectedItems[`additive${num}`])}
                      className="bait-display-image"
                      onError={() => {}}
                    />
                    <span className="bait-display-name">{getDisplayName(selectedItems[`additive${num}`])}</span>
                  </>
                )}
              </div>
            </div>
          ))}

          <div className="bait-display-item">
            <div className="bait-display-header">
              <span className="bait-display-label">유인제</span>
            </div>
            <div className="bait-display-content">
              {selectedItems.attractant && (
                <>
                  <BaitImage 
                    filename={selectedItems.attractant.filename}
                    alt={getDisplayName(selectedItems.attractant)}
                    className="bait-display-image"
                    onError={() => {}}
                  />
                  <span className="bait-display-name">{getDisplayName(selectedItems.attractant)}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* 우측: 선택 패널 */}
        <div className="bait-selection-panel">
          <div className="panel-header">
            <h2>미끼/밑밥 선택</h2>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <div className="language-buttons">
                <button 
                  className="language-btn share-btn"
                  onClick={() => {
                    const url = window.location.href;
                    navigator.clipboard.writeText(url).then(() => {
                      toast.success('공유 링크가 클립보드에 복사되었습니다!', {
                        duration: 2000
                      });
                    }).catch(err => {
                      console.error('URL 복사 실패:', err);
                      toast.error('URL 복사에 실패했습니다.');
                    });
                  }}
                  title="현재 설정을 URL로 공유"
                >
                  🔗 공유
                </button>
              </div>
              <div className="language-buttons">
                {['KR', 'EN', 'CN', 'RU'].map(lang => (
                  <button
                    key={lang}
                    className={`language-btn ${searchLanguage === lang ? 'active' : ''}`}
                    onClick={() => setSearchLanguage(lang)}
                  >
                    {lang}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          <SearchableSelect 
            category="base"
            type="base"
            label="베이스"
            groundbaitData={groundbaitData}
            selectedItems={selectedItems}
            searchTerms={searchTerms}
            openDropdowns={openDropdowns}
            searchLanguage={searchLanguage}
            onSearchChange={handleSearchChange}
            onItemSelect={handleItemSelect}
            onClearSelection={handleClearSelection}
            onToggleDropdown={toggleDropdown}
            onSetOpenDropdowns={setOpenDropdowns}
          />

          <SearchableSelect 
            category="additive1"
            type="additive"
            label="첨가물 1"
            groundbaitData={groundbaitData}
            selectedItems={selectedItems}
            searchTerms={searchTerms}
            openDropdowns={openDropdowns}
            searchLanguage={searchLanguage}
            onSearchChange={handleSearchChange}
            onItemSelect={handleItemSelect}
            onClearSelection={handleClearSelection}
            onToggleDropdown={toggleDropdown}
            onSetOpenDropdowns={setOpenDropdowns}
          />

          <SearchableSelect 
            category="additive2"
            type="additive"
            label="첨가물 2"
            groundbaitData={groundbaitData}
            selectedItems={selectedItems}
            searchTerms={searchTerms}
            openDropdowns={openDropdowns}
            searchLanguage={searchLanguage}
            onSearchChange={handleSearchChange}
            onItemSelect={handleItemSelect}
            onClearSelection={handleClearSelection}
            onToggleDropdown={toggleDropdown}
            onSetOpenDropdowns={setOpenDropdowns}
          />

          <SearchableSelect 
            category="additive3"
            type="additive"
            label="첨가물 3"
            groundbaitData={groundbaitData}
            selectedItems={selectedItems}
            searchTerms={searchTerms}
            openDropdowns={openDropdowns}
            searchLanguage={searchLanguage}
            onSearchChange={handleSearchChange}
            onItemSelect={handleItemSelect}
            onClearSelection={handleClearSelection}
            onToggleDropdown={toggleDropdown}
            onSetOpenDropdowns={setOpenDropdowns}
          />

          <SearchableSelect 
            category="additive4"
            type="additive"
            label="첨가물 4"
            groundbaitData={groundbaitData}
            selectedItems={selectedItems}
            searchTerms={searchTerms}
            openDropdowns={openDropdowns}
            searchLanguage={searchLanguage}
            onSearchChange={handleSearchChange}
            onItemSelect={handleItemSelect}
            onClearSelection={handleClearSelection}
            onToggleDropdown={toggleDropdown}
            onSetOpenDropdowns={setOpenDropdowns}
          />

          <SearchableSelect 
            category="attractant"
            type="attractant"
            label="유인제"
            groundbaitData={groundbaitData}
            selectedItems={selectedItems}
            searchTerms={searchTerms}
            openDropdowns={openDropdowns}
            searchLanguage={searchLanguage}
            onSearchChange={handleSearchChange}
            onItemSelect={handleItemSelect}
            onClearSelection={handleClearSelection}
            onToggleDropdown={toggleDropdown}
            onSetOpenDropdowns={setOpenDropdowns}
          />
        </div>
      </div>

      {/* 가격 계산 정보 */}
      {priceInfo && (
        <div className="price-calculation-panel">
          <h2>제작 비용 계산</h2>
          <div className="price-info-grid">
            <div className="price-info-item">
              <div className="price-label">제작 수량 (1회당)</div>
              <div className="price-value">{priceInfo.totalQuantity}개</div>
              <div className="price-detail">
                베이스 + 유인제 (20개) + 첨가물 {priceInfo.additiveCount}개 (×10개)
              </div>
            </div>
            
            <div className="price-info-item">
              <div className="price-label">총 구매 비용</div>
              <div className="price-value">{priceInfo.totalPrice.toLocaleString()}원</div>
              <div className="price-detail">최소 구매 수량 기준</div>
            </div>
            
            <div className="price-info-item">
              <div className="price-label">1개당 비용</div>
              <div className="price-value">{priceInfo.costPerUse.toFixed(2)}원</div>
              <div className="price-detail">실제 제작량 기준 밑밥 1개당 비용</div>
            </div>
            
            <div className="price-info-item">
              <div className="price-label">PVA 제작 비용</div>
              <div className="pva-cost-grid">
                <div className="pva-cost-item">
                  <div className="pva-weight">10g</div>
                        <div className="pva-cost">{((priceInfo.totalPrice + 3.9) / 60.0).toFixed(2)} 실버</div>
                </div>
                <div className="pva-cost-item">
                  <div className="pva-weight">20g</div>
                        <div className="pva-cost">{((priceInfo.totalPrice + 3.9) / 30.0).toFixed(2)} 실버</div>
                </div>
                <div className="pva-cost-item">
                  <div className="pva-weight">50g</div>
                        <div className="pva-cost">{((priceInfo.totalPrice + 3.9) / 12.0).toFixed(2)} 실버</div>
                </div>
              </div>
              <div className="price-detail">PVA 중량별 필요 비용</div>
            </div>
          </div>
        </div>
      )}

      {/* 삭제 확인 모달 */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="delete-modal">
            <div className="modal-header">
              <h3>설정 삭제 확인</h3>
            </div>
            <div className="modal-content">
              <p>선택한 미끼 설정을 정말로 삭제하시겠습니까?</p>
              <p className="warning-text">삭제된 설정은 복구할 수 없습니다.</p>
            </div>
            <div className="modal-actions">
              <button 
                className="btn-cancel" 
                onClick={cancelDeleteSetting}
              >
                취소
              </button>
              <button 
                className="btn-delete" 
                onClick={confirmDeleteSetting}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 중복 이름 확인 모달 */}
      {showDuplicateModal && (
        <div className="modal-overlay">
          <div className="delete-modal comparison-modal">
            <div className="modal-header">
              <h3>동일한 이름의 설정이 존재합니다</h3>
            </div>
            <div className="modal-content">
              <p><strong>"{duplicateSettingName}"</strong> 이름으로 저장된 설정이 이미 있습니다.</p>
              <p>기존 설정을 새로운 설정으로 덮어쓰시겠습니까?</p>
              
              <div className="settings-comparison">
                {(() => {
                  const existingSetting = savedSettings.find(setting => 
                    setting.saved_name.toLowerCase() === duplicateSettingName.toLowerCase()
                  );
                  
                  const getItemName = (itemId) => {
                    if (!itemId) return '-';
                    const item = groundbaitData.find(item => item.id === itemId);
                    return item ? (item.KR || item.filename) : '-';
                  };

                  return (
                    <div className="comparison-grid">
                      <div className="comparison-section existing">
                        <h4>🔄 기존 설정 (덮어쓰여질 설정)</h4>
                        <div className="setting-details">
                          <div className="detail-item">
                            <span className="label">베이스:</span>
                            <span className="value">{getItemName(existingSetting?.base_id)}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">첨가물 1:</span>
                            <span className="value">{getItemName(existingSetting?.additive1_id)}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">첨가물 2:</span>
                            <span className="value">{getItemName(existingSetting?.additive2_id)}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">첨가물 3:</span>
                            <span className="value">{getItemName(existingSetting?.additive3_id)}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">첨가물 4:</span>
                            <span className="value">{getItemName(existingSetting?.additive4_id)}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">유인제:</span>
                            <span className="value">{getItemName(existingSetting?.attractant_id)}</span>
                          </div>
                          <div className="detail-item date-item">
                            <span className="label">저장일:</span>
                            <span className="value">{new Date(existingSetting?.updated_at).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="comparison-section new">
                        <h4>✨ 새로운 설정 (저장될 설정)</h4>
                        <div className="setting-details">
                          <div className="detail-item">
                            <span className="label">베이스:</span>
                            <span className="value">{selectedItems.base ? (selectedItems.base.KR || selectedItems.base.filename) : '-'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">첨가물 1:</span>
                            <span className="value">{selectedItems.additive1 ? (selectedItems.additive1.KR || selectedItems.additive1.filename) : '-'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">첨가물 2:</span>
                            <span className="value">{selectedItems.additive2 ? (selectedItems.additive2.KR || selectedItems.additive2.filename) : '-'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">첨가물 3:</span>
                            <span className="value">{selectedItems.additive3 ? (selectedItems.additive3.KR || selectedItems.additive3.filename) : '-'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">첨가물 4:</span>
                            <span className="value">{selectedItems.additive4 ? (selectedItems.additive4.KR || selectedItems.additive4.filename) : '-'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">유인제:</span>
                            <span className="value">{selectedItems.attractant ? (selectedItems.attractant.KR || selectedItems.attractant.filename) : '-'}</span>
                          </div>
                          <div className="detail-item date-item">
                            <span className="label">저장일:</span>
                            <span className="value">지금</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className="modal-actions">
              <button 
                className="btn-cancel" 
                onClick={cancelOverwrite}
              >
                취소
              </button>
              <button 
                className="btn-delete" 
                onClick={confirmOverwrite}
              >
                덮어쓰기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 클립보드 검색 모달 */}
      {showClipboardModal && (
        <div className="modal-overlay">
          <div className="clipboard-modal">
            <div className="modal-header">
              <h3>📋 클립보드 미끼 검색</h3>
              <button 
                className="modal-close-btn"
                onClick={() => {
                  setShowClipboardModal(false);
                  setClipboardText('');
                  setSearchResults([]);
                  setSelectedSearchItems({});
                }}
              >
                ✕
              </button>
            </div>
            
            <div className="modal-content clipboard-content">
              <div className="clipboard-input-section">
                <label htmlFor="clipboard-input">미끼 텍스트 붙여넣기:</label>
                <textarea
                  id="clipboard-input"
                  className="clipboard-textarea"
                  placeholder="여기에 미끼 이름 목록을 붙여넣으세요...&#10;예시:&#10;氨基8&#10;氨基 10&#10;螃蟹4&#10;螃蟹7"
                  value={clipboardText}
                  onChange={(e) => setClipboardText(e.target.value)}
                  rows={8}
                />
                <button 
                  className="search-btn"
                  onClick={searchBaitsFromClipboard}
                >
                  🔍 검색
                </button>
              </div>

              {searchResults.length > 0 && (
                <div className="search-results-section">
                  <h4>검색 결과: {searchResults.length}개</h4>
                  
                  {/* 베이스 그룹 */}
                  {(() => {
                    const baseItems = searchResults.filter(r => r.item.type === '베이스');
                    if (baseItems.length === 0) return null;
                    
                    return (
                      <div className="result-group">
                        <h5 className="group-title">베이스 ({baseItems.length}개)</h5>
                        <div className="result-list">
                          {baseItems.map(result => (
                            <div 
                              key={result.item.id} 
                              className="result-item"
                              onClick={() => toggleSearchItemSelection(result.item.id)}
                              style={{ cursor: 'pointer' }}
                            >
                              <input
                                type="checkbox"
                                id={`check-${result.item.id}`}
                                checked={selectedSearchItems[result.item.id] || false}
                                onChange={() => toggleSearchItemSelection(result.item.id)}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <BaitImage 
                                filename={result.item.filename}
                                alt={result.item.KR}
                                className="result-image"
                                onError={() => {}}
                              />
                              <div className="result-info">
                                <div className="result-name-kr">{result.item.KR}</div>
                                <div className="result-name-matched">
                                  검색어: {result.searchText} → {result.matchedLang}: {result.matchedName} ({result.similarity.toFixed(0)}% 일치)
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* 첨가물 그룹 (싱킹 보일리, 팝업 보일리, 펠릿, 첨가제) */}
                  {(() => {
                    const additiveItems = searchResults.filter(r => 
                      ['첨가물', '싱킹 보일리', '팝업 보일리', '음식', '펠릿'].includes(r.item.type)
                    );
                    if (additiveItems.length === 0) return null;
                    
                    return (
                      <div className="result-group">
                        <h5 className="group-title">첨가물 ({additiveItems.length}개)</h5>
                        <div className="result-list">
                          {additiveItems.map(result => (
                            <div 
                              key={result.item.id} 
                              className="result-item"
                              onClick={() => toggleSearchItemSelection(result.item.id)}
                              style={{ cursor: 'pointer' }}
                            >
                              <input
                                type="checkbox"
                                id={`check-${result.item.id}`}
                                checked={selectedSearchItems[result.item.id] || false}
                                onChange={() => toggleSearchItemSelection(result.item.id)}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <BaitImage 
                                filename={result.item.filename}
                                alt={result.item.KR}
                                className="result-image"
                                onError={() => {}}
                              />
                              <div className="result-info">
                                <div className="result-name-kr">{result.item.KR}</div>
                                <div className="result-name-matched">
                                  검색어: {result.searchText} → {result.matchedLang}: {result.matchedName} ({result.similarity.toFixed(0)}% 일치)
                                </div>
                                <div className="result-type">{result.item.type}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* 유인제 그룹 */}
                  {(() => {
                    const attractantItems = searchResults.filter(r => r.item.type === '유인제');
                    if (attractantItems.length === 0) return null;
                    
                    return (
                      <div className="result-group">
                        <h5 className="group-title">유인제 ({attractantItems.length}개)</h5>
                        <div className="result-list">
                          {attractantItems.map(result => (
                            <div 
                              key={result.item.id} 
                              className="result-item"
                              onClick={() => toggleSearchItemSelection(result.item.id)}
                              style={{ cursor: 'pointer' }}
                            >
                              <input
                                type="checkbox"
                                id={`check-${result.item.id}`}
                                checked={selectedSearchItems[result.item.id] || false}
                                onChange={() => toggleSearchItemSelection(result.item.id)}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <BaitImage 
                                filename={result.item.filename}
                                alt={result.item.KR}
                                className="result-image"
                                onError={() => {}}
                              />
                              <div className="result-info">
                                <div className="result-name-kr">{result.item.KR}</div>
                                <div className="result-name-matched">
                                  검색어: {result.searchText} → {result.matchedLang}: {result.matchedName} ({result.similarity.toFixed(0)}% 일치)
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button 
                className="btn-cancel" 
                onClick={() => {
                  setShowClipboardModal(false);
                  setClipboardText('');
                  setSearchResults([]);
                  setSelectedSearchItems({});
                }}
              >
                취소
              </button>
              <button 
                className="btn-apply" 
                onClick={applySearchResults}
                disabled={Object.keys(selectedSearchItems).filter(id => selectedSearchItems[id]).length === 0}
              >
                불러오기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BaitCalculator;
